import { buildPrompt } from '@/ai/utils/promptBuilder'
import { domainEventNotes } from '@/ai/prompts/common/instructions'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { examples, getEventExample } from '@/ai/prompts/common/examples'
import { createSystemMessage, createUserMessage } from '@/ai/utils/promptUtils'
import { buildWorkflowSpec, buildCardTypesMap } from '@/shared/utils/workflowSpec'
import { wrapUserInput } from '@/ai/utils/helpers'
import { getJsonArrayOutputFormatting, getJsonOutputFormatting } from '@/ai/prompts/common/formatting'

export const buildWorkflowDomainEventsPrompt = ({ workflow, cardTypes, stream = true }) => {
  const messages = []

  const systemContent = buildPrompt([systemMessages.EVENT_MODELING_DDD_EXPERT])
  messages.push(createSystemMessage(systemContent))

  const promptSections = []

  promptSections.push({
    title: 'Request',
    content: 'For each domain event in the workflow, generate the data fields that the domain event carries as its payload. A domain event represents something that happened in the system — it captures the essential facts about the state change. The fields should describe WHAT happened, not the full entity state. Include identifier fields (e.g., orderId, cartId) so event consumers can correlate the event to the aggregate. Include timestamp fields when timing is business-relevant. When the event carries nested data (e.g., order items), model nested fields with sub-fields. Field names must be in camelCase and at most 30 characters long. For each domain event, also provide the name of the entity (aggregate root) that produced the event (domainEventEntity). The domainEventEntity SHOULD preferably be one of the entity names from the workflow specification.'
  })

  promptSections.push({
    title: 'Workflow Description',
    flattenLines: false,
    content: wrapUserInput(workflow.aiDescription)
  })

  const cardTypesMap = buildCardTypesMap(cardTypes)

  promptSections.push({
    title: 'Current Workflow Specification (JSON format)',
    contentType: 'codeblock',
    language: 'json',
    content: buildWorkflowSpec(workflow, null, cardTypesMap) || '{}'
  })

  promptSections.push({
    title: 'Very important considerations',
    contentType: 'bullet',
    content: domainEventNotes
  })

  const formattingContent = `The resulting array must contain ${workflow.domainEvents.length} items.`

  const options = {
    shouldAddCards: false,
    shouldAddSchemas: true
  }

  const exampleData1 = {
    name: 'Order Placed',
    domainEventExample: examples.domainEventExampleOrderPlaced,
    domainEventEntity: 'Order'
  }
  const exampleData2 = {
    name: 'Item Added to Cart',
    domainEventExample: examples.domainEventExampleItemAddedToCart,
    domainEventEntity: 'Cart'
  }

  const eventsExample = [
    getEventExample(exampleData1, cardTypes, options),
    getEventExample(exampleData2, cardTypes, options)
  ]

  const eventsObject = { events: eventsExample }

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: stream
      ? getJsonArrayOutputFormatting(JSON.stringify(eventsExample), formattingContent)
      : getJsonOutputFormatting('events', JSON.stringify(eventsObject), formattingContent)
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return { messages }
}
